import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { TrendingUp, ArrowLeft, Heart, Clock, User, MessageSquare, Send, Trash2, Menu } from 'lucide-react';
import { fetchApi, formatTimeAgo, formatPrice, cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function PostDetailPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [commenting, setCommenting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const data = await fetchApi(`/forum/posts/${postId}`);
      setPost(data.post);
    } catch (error) {
      toast.error('Failed to load post');
      navigate('/forum');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to like posts');
      return;
    }
    
    try {
      const data = await fetchApi(`/forum/posts/${postId}/like`, { method: 'POST' });
      setPost(prev => ({
        ...prev,
        likes_count: data.likes_count,
        liked_by: data.action === 'liked' 
          ? [...(prev.liked_by || []), user?.user_id]
          : (prev.liked_by || []).filter(id => id !== user?.user_id)
      }));
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  // Validate English-only text
  const isEnglishOnly = (text) => {
    const pattern = /^[a-zA-Z0-9\s\.\,\!\?\'\"\-\:\;\(\)\[\]\@\#\$\%\&\*\+\=\/\\\n\r]*$/;
    return pattern.test(text);
  };

  const handleComment = async () => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }
    
    if (!isAuthenticated) {
      toast.error('Please sign in to comment');
      return;
    }
    
    if (newComment.length > 2000) {
      toast.error('Comment must be 2000 characters or less');
      return;
    }
    
    if (!isEnglishOnly(newComment)) {
      toast.error('Comments must contain only English characters');
      return;
    }
    
    setCommenting(true);
    try {
      const data = await fetchApi(`/forum/posts/${postId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ content: newComment })
      });
      setPost(prev => ({
        ...prev,
        comments: [...(prev.comments || []), data.comment],
        comments_count: (prev.comments_count || 0) + 1
      }));
      setNewComment('');
      toast.success('Comment added!');
    } catch (error) {
      toast.error(error.message || 'Failed to add comment. Please try again.');
    } finally {
      setCommenting(false);
    }
  };

  const handleDeletePost = async () => {
    try {
      await fetchApi(`/forum/posts/${postId}`, { method: 'DELETE' });
      toast.success('Post deleted');
      navigate('/forum');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await fetchApi(`/forum/comments/${commentId}`, { method: 'DELETE' });
      setPost(prev => ({
        ...prev,
        comments: prev.comments.filter(c => c.comment_id !== commentId),
        comments_count: (prev.comments_count || 1) - 1
      }));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <nav className="sticky top-0 z-50 glass border-b border-border/50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <Skeleton className="h-8 w-32" />
          </div>
        </nav>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
          <Skeleton className="h-8 w-3/4 mb-4" />
          <Skeleton className="h-4 w-1/4 mb-6" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (!post) return null;

  const isAuthor = user?.user_id === post.user_id;
  const isLiked = post.liked_by?.includes(user?.user_id);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/forum')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-sm bg-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-heading font-bold text-lg hidden sm:inline">MarketMind</span>
            </Link>
          </div>
          
          <div className="hidden md:flex items-center gap-4">
            <Link to="/forum" className="text-muted-foreground hover:text-foreground">Forum</Link>
            <Link to="/stocks" className="text-muted-foreground hover:text-foreground">Stocks</Link>
          </div>

          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] bg-card">
              <SheetHeader>
                <SheetTitle className="text-left">Menu</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-4 mt-6">
                <Link to="/forum" onClick={() => setMobileMenuOpen(false)} className="py-2">Forum</Link>
                <Link to="/stocks" onClick={() => setMobileMenuOpen(false)} className="py-2">Stocks</Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Post */}
        <article className="mb-8">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1">
              {post.ticker && (
                <Badge 
                  variant="outline" 
                  className="font-mono mb-2 cursor-pointer hover:bg-primary/10"
                  onClick={() => navigate(`/stocks/${post.ticker}`)}
                >
                  ${post.ticker}
                  {post.stock_name && <span className="text-muted-foreground ml-1">• {post.stock_name}</span>}
                  {post.stock_price && <span className="text-primary ml-1">• {formatPrice(post.stock_price)}</span>}
                </Badge>
              )}
              <h1 className="font-heading font-bold text-xl sm:text-2xl md:text-3xl tracking-tight">
                {post.title}
              </h1>
            </div>
            
            {isAuthor && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Post?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. All comments will also be deleted.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeletePost} className="bg-red-500 hover:bg-red-600">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {post.author_name}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {formatTimeAgo(post.created_at)}
            </span>
          </div>
          
          <div className="prose prose-invert max-w-none mb-6">
            <p className="text-base sm:text-lg whitespace-pre-wrap leading-relaxed">
              {post.content}
            </p>
          </div>
          
          <div className="flex items-center gap-4 pt-4 border-t border-border/50">
            <Button
              variant="ghost"
              size="sm"
              className={cn(isLiked && "text-red-500")}
              onClick={handleLike}
            >
              <Heart className={cn("w-4 h-4 mr-1", isLiked && "fill-current")} />
              {post.likes_count || 0} likes
            </Button>
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <MessageSquare className="w-4 h-4" />
              {post.comments_count || 0} comments
            </span>
          </div>
        </article>

        {/* Comments Section */}
        <section>
          <h2 className="font-heading font-semibold text-lg mb-4">Comments</h2>
          
          {/* Add Comment */}
          <Card className="bg-card border-border/50 mb-6">
            <CardContent className="p-4">
              <Textarea
                data-testid="comment-input"
                placeholder={isAuthenticated ? "Share your thoughts..." : "Sign in to comment"}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                disabled={!isAuthenticated}
                rows={3}
                className="mb-3"
              />
              <div className="flex justify-end">
                <Button 
                  data-testid="submit-comment-btn"
                  onClick={handleComment} 
                  disabled={!isAuthenticated || commenting || !newComment.trim()}
                  size="sm"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {commenting ? 'Posting...' : 'Post Comment'}
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Comments List */}
          <div className="space-y-4">
            {(!post.comments || post.comments.length === 0) ? (
              <p className="text-center text-muted-foreground py-8">
                No comments yet. Be the first to share your thoughts!
              </p>
            ) : (
              post.comments.map((comment) => (
                <Card key={comment.comment_id} className="bg-card/50 border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <User className="w-3 h-3" />
                        <span className="font-medium text-foreground">{comment.author_name}</span>
                        <span>•</span>
                        <span>{formatTimeAgo(comment.created_at)}</span>
                      </div>
                      {user?.user_id === comment.user_id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-red-500">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Comment?</AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDeleteComment(comment.comment_id)} 
                                className="bg-red-500 hover:bg-red-600"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{comment.content}</p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
